// src/pages/events/EventPaymentsDialog.js
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Typography,
  CircularProgress,
  Box,
  Divider,
  IconButton,
  Tooltip,
  Paper,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import {
  Payment as PaymentIcon,
  Email as EmailIcon,
  Close as CloseIcon,
  Download as DownloadIcon,
  Euro as EuroIcon
} from '@mui/icons-material';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../../../firebase';

const EventPaymentsDialog = ({ open, onClose, eventId, eventTitle, eventPrice }) => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalRevenue, setTotalRevenue] = useState(0);

  useEffect(() => {
    if (open && eventId) {
      fetchPayments();
    }
  }, [open, eventId]);

  const fetchPayments = async () => {
    setLoading(true);
    try {
      // Obtener todos los tickets del evento desde eventTickets
      const ticketsQuery = query(
        collection(db, 'eventTickets'),
        where('eventId', '==', eventId),
        where('status', '==', 'purchased')
      );

      const ticketsSnapshot = await getDocs(ticketsQuery);
      const paymentsData = [];
      let revenue = 0;

      for (const ticketDoc of ticketsSnapshot.docs) {
        const ticketData = ticketDoc.data();

        // Obtener información del usuario (padre)
        try {
          const userDoc = await getDoc(doc(db, 'users', ticketData.userId));
          let userData = {};

          if (userDoc.exists()) {
            userData = userDoc.data();
          }

          // Obtener información del hijo usando parentId
          let childCourse = 'No especificado';
          if (ticketData.userId && ticketData.childName) {
            try {
              // Buscar hijos donde parentId === userId
              const childrenQuery = query(
                collection(db, 'children'),
                where('parentId', '==', ticketData.userId)
              );
              const childrenSnapshot = await getDocs(childrenQuery);

              // Buscar el hijo que coincida con childName
              childrenSnapshot.forEach(childDoc => {
                const childData = childDoc.data();
                if (childData.nombre === ticketData.childName) {
                  console.log(childData.curso)
                  childCourse = childData.curso || 'No especificado';
                }
              });
            } catch (error) {
              console.error('Error loading child data:', error);
            }
          }

          const payment = {
            id: ticketDoc.id,
            ...ticketData,
            userName: `${userData.firstName || ''} ${userData.lastName || ''}`.trim() || 'Usuario desconocido',
            userEmail: userData.email || ticketData.userEmail || 'Email no disponible',
            userPhone: userData.phone || '',
            childName: ticketData.childName || 'No especificado',
            childCourse: childCourse,
            amount: ticketData.price || 0
          };

          paymentsData.push(payment);
          revenue += payment.amount;
        } catch (error) {
          console.error('Error loading ticket user data:', error);
          // Añadir el ticket sin datos de usuario
          paymentsData.push({
            id: ticketDoc.id,
            ...ticketData,
            userName: 'Usuario desconocido',
            userEmail: ticketData.userEmail || 'Email no disponible',
            childName: ticketData.childName || 'No especificado',
            childCourse: 'No especificado',
            amount: ticketData.price || 0
          });
          revenue += ticketData.price || 0;
        }
      }

      // Ordenar por fecha de compra (más recientes primero)
      paymentsData.sort((a, b) => {
        const dateA = a.purchaseDate?.seconds || a.createdAt?.seconds || 0;
        const dateB = b.purchaseDate?.seconds || b.createdAt?.seconds || 0;
        return dateB - dateA;
      });

      setPayments(paymentsData);
      setTotalRevenue(revenue);
    } catch (error) {
      console.error('Error fetching tickets:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp || !timestamp.seconds) return 'Fecha no disponible';
    return new Date(timestamp.seconds * 1000).toLocaleString('es-ES', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const sanitizeCsvField = (value) => {
    if (value == null) return '""';
    let str = String(value);
    // Prefix values starting with formula characters to prevent CSV injection
    if (/^[=+\-@]/.test(str)) {
      str = "'" + str;
    }
    // Escape double quotes by doubling them, then wrap in double quotes
    str = str.replace(/"/g, '""');
    return `"${str}"`;
  };

  const handleExportCSV = () => {
    if (payments.length === 0) return;

    const headers = ['Nombre Usuario', 'Email', 'Nombre Hijo', 'Curso', 'Importe', 'Fecha', 'Número Ticket', 'Estado'];
    const csvContent = [
      headers.join(','),
      ...payments.map(payment => [
        sanitizeCsvField(payment.userName),
        sanitizeCsvField(payment.userEmail),
        sanitizeCsvField(payment.childName),
        sanitizeCsvField(payment.childCourse),
        payment.amount?.toFixed(2) || '0.00',
        sanitizeCsvField(formatDate(payment.purchaseDate || payment.createdAt)),
        sanitizeCsvField(payment.ticketNumber || ''),
        sanitizeCsvField(payment.status || 'purchased')
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `tickets_${eventTitle.replace(/[^a-zA-Z0-9]/g, '_')}_${Date.now()}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            Pagos del Evento - {eventTitle}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" py={4}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Resumen de pagos */}
            <Paper sx={{ p: 3, mb: 3, bgcolor: 'success.light', color: 'success.contrastText' }}>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="h4">
                    {totalRevenue.toFixed(2)} €
                  </Typography>
                  <Typography variant="h6">
                    Total recaudado
                  </Typography>
                  <Typography variant="body2">
                    {payments.length} pago{payments.length !== 1 ? 's' : ''} • 
                    Precio por entrada: {eventPrice?.toFixed(2)} €
                  </Typography>
                </Box>
                <Box>
                  <EuroIcon sx={{ fontSize: 48 }} />
                </Box>
              </Box>
            </Paper>

            {payments.length === 0 ? (
              <Box textAlign="center" py={4}>
                <PaymentIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary">
                  No hay tickets registrados
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Los tickets aparecerán aquí cuando los usuarios compren entradas desde el portal.
                </Typography>
              </Box>
            ) : (
              <>
                {/* Botón de exportar */}
                <Box display="flex" justifyContent="flex-end" mb={2}>
                  <Button
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    onClick={handleExportCSV}
                  >
                    Exportar CSV
                  </Button>
                </Box>

                {/* Tabla de pagos */}
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Usuario</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell>Hijo/a</TableCell>
                        <TableCell>Curso</TableCell>
                        <TableCell align="right">Importe</TableCell>
                        <TableCell>Fecha</TableCell>
                        <TableCell>Ticket</TableCell>
                        <TableCell>Estado</TableCell>
                        <TableCell align="center">Acciones</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {payments.map((payment) => (
                        <TableRow key={payment.id}>
                          <TableCell>
                            <Box display="flex" alignItems="center">
                              <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                                {payment.userName?.charAt(0) || 'U'}
                              </Avatar>
                              <Box>
                                <Typography variant="subtitle2">
                                  {payment.userName}
                                </Typography>
                                {payment.userPhone && (
                                  <Typography variant="caption" color="text.secondary">
                                    {payment.userPhone}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          </TableCell>

                          <TableCell>
                            <Typography variant="body2">
                              {payment.userEmail}
                            </Typography>
                          </TableCell>

                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {payment.childName}
                            </Typography>
                          </TableCell>

                          <TableCell>
                            <Chip
                              label={payment.childCourse}
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                          </TableCell>

                          <TableCell align="right">
                            <Typography variant="subtitle1" fontWeight="bold" color="success.main">
                              {payment.amount?.toFixed(2) || '0.00'} €
                            </Typography>
                          </TableCell>

                          <TableCell>
                            <Typography variant="body2">
                              {formatDate(payment.purchaseDate || payment.createdAt)}
                            </Typography>
                          </TableCell>

                          <TableCell>
                            <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                              {payment.ticketNumber || 'No disponible'}
                            </Typography>
                          </TableCell>

                          <TableCell>
                            <Chip
                              label="Comprado"
                              color="success"
                              size="small"
                            />
                          </TableCell>

                          <TableCell align="center">
                            {payment.userEmail && (
                              <Tooltip title="Enviar email">
                                <IconButton
                                  size="small"
                                  onClick={() => window.open(`mailto:${payment.userEmail}`)}
                                >
                                  <EmailIcon />
                                </IconButton>
                              </Tooltip>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
          </>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose}>
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EventPaymentsDialog;